import { useEffect, useState } from "react";
import { Plus, Search, Trash2, Sparkles, FileText, CalendarPlus } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, API, fmtDate, formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { useSearchableList } from "@/hooks/useSearchableList";
import { PageHeader, Field, EmptyState, CopyButton, inputCls, RefBadge, withRef } from "@/components/shared";

const emptyViewing = {
  buyer_id: "", buyer_name: "", property_id: "", property_name: "", date_time: "",
  general_reaction: "", liked: "", concerns: "", other_properties: "", decision_date: "", next_action: "",
};

export default function Viewings() {
  const { items, search, setSearch, reload } = useSearchableList("/viewings");
  const [buyers, setBuyers] = useState([]);
  const [properties, setProperties] = useState([]);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [generatingId, setGeneratingId] = useState(null);
  const [report, setReport] = useState(null);
  const { t, lang } = useLang();

  useEffect(() => {
    api.get("/buyers").then((r) => setBuyers(r.data)).catch(() => {});
    api.get("/properties").then((r) => setProperties(r.data)).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const save = async () => {
    if (!form.buyer_name.trim() || !form.property_name.trim()) return toast.error(t("required"));
    setSaving(true);
    try {
      if (form.id) await api.put(`/viewings/${form.id}`, form);
      else await api.post("/viewings", form);
      toast.success(t("saved"));
      setForm(null);
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(t("confirmDelete"))) return;
    await api.delete(`/viewings/${form.id}`);
    toast.success(t("deleted"));
    setForm(null);
    reload();
  };

  const generateReport = async (viewing) => {
    setGeneratingId(viewing.id);
    try {
      const { data } = await api.post(`/ai/viewing-report/${viewing.id}`, {}, { timeout: 120000 });
      setReport({ ...data, viewing });
      toast.success(t("reportReady"));
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setGeneratingId(null);
    }
  };

  return (
    <div data-testid="viewings-page">
      <PageHeader
        title={t("viewings")}
        action={
          <button data-testid="add-viewing-button" onClick={() => setForm({ ...emptyViewing })}
            className="inline-flex items-center gap-2 bg-slate-900 text-white hover:bg-slate-800 rounded-lg px-5 py-2.5 text-sm font-medium transition-colors">
            <Plus size={16} strokeWidth={1.5} /> {t("newViewing")}
          </button>
        }
      />
      <div className="relative mb-6 max-w-md">
        <Search size={16} strokeWidth={1.5} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
        <input data-testid="viewings-search-input" value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder={t("search")} className={`${inputCls} pl-9`} />
      </div>

      {items.length === 0 ? <EmptyState message={t("noViewings")} /> : (
        <div className="space-y-4">
          {items.map((v) => {
            const buyerRef = (buyers.find((b) => b.id === v.buyer_id) || {}).ref_code;
            const propertyRef = (properties.find((p) => p.id === v.property_id) || {}).ref_code;
            return (
            <div key={v.id} data-testid={`viewing-card-${v.id}`} className="bg-white border border-stone-200 rounded-xl p-5 hover:border-stone-300 transition-all duration-200">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <button className="text-left" onClick={() => setForm({ ...emptyViewing, ...v })} data-testid={`viewing-edit-${v.id}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <RefBadge code={buyerRef} />
                    <span className="text-stone-300 text-xs">·</span>
                    <RefBadge code={propertyRef} />
                  </div>
                  <h3 className="font-heading text-lg font-medium text-stone-900">{v.buyer_name}</h3>
                  <p className="text-sm text-stone-500">{v.property_name}</p>
                  <p className="text-xs text-stone-400 mt-1">
                    {v.date_time ? `${fmtDate(v.date_time, lang)} ${v.date_time.slice(11, 16)}` : "—"}
                    {v.decision_date ? ` · ${t("decisionBy")} ${fmtDate(v.decision_date, lang)}` : ""}
                  </p>
                  {v.general_reaction && <p className="text-sm text-stone-600 mt-2 line-clamp-1">{v.general_reaction}</p>}
                </button>
                <div className="flex flex-wrap items-center gap-2">
                  {v.date_time && (
                    <a data-testid={`ics-viewing-${v.id}`} href={`${API}/viewings/${v.id}/ics`}
                      className="inline-flex items-center gap-1.5 text-xs font-medium text-stone-600 border border-stone-200 hover:bg-stone-50 rounded-lg px-3 py-2 transition-colors">
                      <CalendarPlus size={14} strokeWidth={1.5} /> {t("addToCalendar")}
                    </a>
                  )}
                  {v.report_en && (
                    <button data-testid={`view-report-${v.id}`}
                      onClick={() => setReport({ report_en: v.report_en, report_fr: v.report_fr, hestia_notes: v.hestia_notes, viewing: v })}
                      className="inline-flex items-center gap-1.5 text-xs font-medium text-stone-600 border border-stone-200 hover:bg-stone-50 rounded-lg px-3 py-2 transition-colors">
                      <FileText size={14} strokeWidth={1.5} /> {t("viewReport")}
                    </button>
                  )}
                  <button
                    data-testid={`generate-report-${v.id}`}
                    onClick={() => generateReport(v)}
                    disabled={generatingId === v.id}
                    className="inline-flex items-center gap-1.5 bg-[#2F4F4F] hover:bg-[#243c3c] text-white rounded-lg px-4 py-2 text-xs font-medium transition-colors disabled:opacity-60">
                    <Sparkles size={14} strokeWidth={1.5} />
                    {generatingId === v.id ? t("generating") : t("generateReport")}
                  </button>
                </div>
              </div>
            </div>
            );
          })}
        </div>
      )}

      {/* Viewing form dialog */}
      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="max-w-2xl bg-white">
          {form && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl font-medium text-stone-900">
                  {form.id ? t("editViewing") : t("newViewing")}
                </DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
                <Field label={t("buyer")}>
                  <Select value={form.buyer_id || "custom"}
                    onValueChange={(v) => {
                      const b = buyers.find((x) => x.id === v);
                      setForm({ ...form, buyer_id: v === "custom" ? "" : v, buyer_name: b ? b.name : form.buyer_name });
                    }}>
                    <SelectTrigger data-testid="viewing-buyer-select" className={inputCls}>
                      <SelectValue placeholder={t("buyer")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="custom">—</SelectItem>
                      {buyers.map((b) => <SelectItem key={b.id} value={b.id}>{withRef(b.ref_code, b.name)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <input data-testid="viewing-buyer-name-input" className={`${inputCls} mt-2`} placeholder={t("name")}
                    value={form.buyer_name} onChange={set("buyer_name")} />
                </Field>
                <Field label={t("property")}>
                  <Select value={form.property_id || "custom"}
                    onValueChange={(v) => {
                      const p = properties.find((x) => x.id === v);
                      setForm({ ...form, property_id: v === "custom" ? "" : v, property_name: p ? p.name : form.property_name });
                    }}>
                    <SelectTrigger data-testid="viewing-property-select" className={inputCls}>
                      <SelectValue placeholder={t("property")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="custom">—</SelectItem>
                      {properties.map((p) => <SelectItem key={p.id} value={p.id}>{withRef(p.ref_code, p.name)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <input data-testid="viewing-property-name-input" className={`${inputCls} mt-2`} placeholder={t("name")}
                    value={form.property_name} onChange={set("property_name")} />
                </Field>
                <Field label={t("dateTime")}><input data-testid="viewing-datetime-input" type="datetime-local" className={inputCls} value={form.date_time} onChange={set("date_time")} /></Field>
                <Field label={t("decisionDate")}><input data-testid="viewing-decision-input" type="date" className={inputCls} value={form.decision_date} onChange={set("decision_date")} /></Field>
                <Field label={t("generalReaction")} className="sm:col-span-2"><textarea data-testid="viewing-reaction-input" rows={2} className={inputCls} value={form.general_reaction} onChange={set("general_reaction")} /></Field>
                <Field label={t("liked")}><textarea data-testid="viewing-liked-input" rows={2} className={inputCls} value={form.liked} onChange={set("liked")} /></Field>
                <Field label={t("concerns")}><textarea data-testid="viewing-concerns-input" rows={2} className={inputCls} value={form.concerns} onChange={set("concerns")} /></Field>
                <Field label={t("otherProperties")}><input data-testid="viewing-other-input" className={inputCls} value={form.other_properties} onChange={set("other_properties")} /></Field>
                <Field label={t("nextAction")}>
                  <input data-testid="viewing-nextaction-input" className={inputCls} value={form.next_action} onChange={set("next_action")} />
                  <p className="text-[11px] text-stone-400 mt-1">{t("autoTaskHint")}</p>
                </Field>
              </div>
              <div className="flex items-center justify-between mt-6">
                {form.id ? (
                  <button data-testid="delete-viewing-button" onClick={remove} className="inline-flex items-center gap-1.5 text-xs font-medium text-[#7F1D1D] hover:underline">
                    <Trash2 size={14} strokeWidth={1.5} /> {t("delete")}
                  </button>
                ) : <span />}
                <div className="flex gap-3">
                  <button data-testid="cancel-viewing-button" onClick={() => setForm(null)} className="bg-white text-stone-700 border border-stone-200 hover:bg-stone-50 rounded-lg px-5 py-2 text-sm font-medium transition-colors">{t("cancel")}</button>
                  <button data-testid="save-viewing-button" onClick={save} disabled={saving} className="bg-slate-900 text-white hover:bg-slate-800 rounded-lg px-5 py-2 text-sm font-medium transition-colors disabled:opacity-60">{t("save")}</button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Report dialog */}
      <Dialog open={!!report} onOpenChange={(o) => !o && setReport(null)}>
        <DialogContent className="max-w-3xl bg-white">
          {report && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl font-medium text-stone-900">
                  {report.viewing?.buyer_name} — {report.viewing?.property_name}
                </DialogTitle>
              </DialogHeader>
              <Tabs defaultValue="en" className="mt-2">
                <TabsList className="bg-stone-100 h-auto p-1">
                  <TabsTrigger value="en" data-testid="report-tab-en">{t("englishReport")}</TabsTrigger>
                  <TabsTrigger value="fr" data-testid="report-tab-fr">{t("frenchReport")}</TabsTrigger>
                  <TabsTrigger value="hestia" data-testid="report-tab-hestia">{t("hestiaNotes")}</TabsTrigger>
                </TabsList>
                {[["en", report.report_en], ["fr", report.report_fr], ["hestia", report.hestia_notes]].map(([key, text]) => (
                  <TabsContent key={key} value={key}>
                    <div className="mt-3">
                      <div className="flex justify-end mb-2">
                        <CopyButton text={text} testId={`copy-report-${key}-button`} />
                      </div>
                      <pre data-testid={`report-${key}-content`} className="whitespace-pre-wrap font-body text-sm text-stone-700 bg-stone-50 border border-stone-200 rounded-xl p-5 leading-relaxed">
                        {text || "—"}
                      </pre>
                    </div>
                  </TabsContent>
                ))}
              </Tabs>
              {report.task && (
                <p className="text-xs text-stone-500 mt-4 bg-stone-50 border border-stone-200 rounded-lg px-3 py-2">
                  ✓ {t("tasks")}: {report.task.title} — {fmtDate(report.task.due_date, lang)}
                </p>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
